import { type IMsalContext, useMsal as msal } from '@azure/msal-react'
import type { Merge, Simplify } from '@festina-life/core'

import { getAuthenticationActions } from './getAuthenticationActions'

type Instance = IMsalContext['instance']

export type IMsalContextExtended = Simplify<ReturnType<typeof useMsal>>

/**
 * We extend the useMsal hook from the MSAL library to include additional authentication actions consistenly that change based on the cache location.
 * @returns The MSAL context with additional authentication actions and actions on the instance marked as deprecated to prevent accidental usage. I tried removing them but meant that e.g. setupServices would have to be updated to only get a partial instance so I used deprecation annotation instead.
 */
export const useMsal = (): Merge<
  IMsalContext,
  {
    instance: Merge<
      Instance,
      {
        /**
         * @deprecated Please use the `login` method returned from this hook instead
         */
        loginPopup: Instance['loginPopup']
        /**
         * @deprecated Please use the `login` method returned from this hook instead
         */
        loginRedirect: Instance['loginRedirect']
        /**
         * @deprecated Please use the `logout` method returned from this hook instead
         */
        logoutRedirect: Instance['logoutRedirect']
        /**
         * @deprecated Please use the `logout` method returned from this hook instead
         */
        logoutPopup: Instance['logoutPopup']
        /**
         * @deprecated Please use the `acquireTokenInteractively` method returned from this hook instead
         */
        acquireTokenRedirect: Instance['acquireTokenRedirect']
        /**
         * @deprecated Please use the `acquireTokenInteractively` method returned from this hook instead
         */
        acquireTokenPopup: Instance['acquireTokenPopup']
      }
    >
  }
> &
  ReturnType<typeof getAuthenticationActions> => {
  const { instance, ...rest } = msal()
  const authActions = getAuthenticationActions(instance)

  return { instance, ...rest, ...authActions }
}
